import { useState } from 'react';
import { cn } from './cn.js';

const FIELD =
  'w-full rounded-lg border border-border bg-card px-3.5 py-2 text-sm text-ink transition-all placeholder:text-ink-ghost hover:border-border-strong focus:border-herb-600 focus:ring-2 focus:ring-herb-600/15 outline-none shadow-xs';

/** Modern text / number input. */
export function Input({
  className,
  ...rest
}: React.ComponentPropsWithRef<'input'>): React.ReactElement {
  return <input {...rest} className={cn(FIELD, className)} />;
}

export function Select({
  className,
  children,
  ...rest
}: React.SelectHTMLAttributes<HTMLSelectElement>): React.ReactElement {
  return (
    <span className="relative inline-flex w-full items-center">
      <select {...rest} className={cn(FIELD, 'w-full appearance-none pr-9', className)}>
        {children}
      </select>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-3 text-[10px] text-ink-faint"
      >
        ▼
      </span>
    </span>
  );
}

export interface FieldProps {
  readonly label: string;
  readonly htmlFor?: string;
  readonly required?: boolean;
  readonly error?: boolean;
  readonly hint?: string;
  readonly children: React.ReactNode;
  readonly className?: string;
}

export function Field({
  label,
  htmlFor,
  required,
  error,
  hint,
  children,
  className,
}: FieldProps): React.ReactElement {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={htmlFor}
        className={cn('text-xs font-semibold uppercase tracking-wider', error === true ? 'text-rust-600' : 'text-ink-soft')}
      >
        {label}
        {required === true && <span className="text-clay-600"> *</span>}
      </label>
      {children}
      {hint !== undefined && <p className="text-xs text-ink-faint">{hint}</p>}
    </div>
  );
}

export function Checkbox({
  className,
  label,
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & {
  readonly label: React.ReactNode;
}): React.ReactElement {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2.5 text-[13px] text-ink-soft select-none">
      <input
        {...rest}
        type="checkbox"
        className={cn('h-4 w-4 rounded border-border-strong text-herb-600 focus:ring-herb-500/20 accent-herb-600', className)}
      />
      {label}
    </label>
  );
}

export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly count?: number;
  readonly tone?: 'signal' | 'scan';
}

export interface SegmentedProps<T extends string> {
  readonly value: T;
  readonly options: readonly SegmentedOption<T>[];
  readonly onChange: (value: T) => void;
  readonly ariaLabel?: string;
  readonly className?: string;
}

/** Modern segmented switch: one row of mutually exclusive choices with active card pill. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
}: SegmentedProps<T>): React.ReactElement {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex flex-wrap items-center gap-1 rounded-lg border border-border bg-cream p-1 shadow-xs',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs sm:text-[13px] font-medium transition-all duration-150 select-none',
              active
                ? 'bg-card text-ink font-semibold shadow-xs'
                : 'text-ink-soft hover:text-ink hover:bg-card/50',
            )}
          >
            {option.label}
            {option.count !== undefined && (
              <span
                className={cn(
                  'tabular text-[11px] font-mono rounded-full px-1.5 py-0.2',
                  active ? 'bg-cream text-ink-soft' : 'text-ink-ghost',
                )}
              >
                {option.count.toLocaleString('fr-FR')}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export interface FilePickProps {
  readonly inputRef: React.RefObject<HTMLInputElement | null>;
  readonly accept?: string;
  readonly title: string;
  readonly hint?: string;
  readonly icon?: React.ReactNode;
}

/** Modern dashed drop-zone file picker. */
export function FilePick({
  inputRef,
  accept,
  title,
  hint,
  icon,
}: FilePickProps): React.ReactElement {
  const [filename, setFilename] = useState('');
  return (
    <label className="group flex min-w-0 cursor-pointer items-center gap-3 rounded-card border border-dashed border-border-strong bg-card px-4 py-3.5 shadow-xs transition-all hover:border-herb-600 hover:bg-herb-50/30">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        required
        className="sr-only"
        onChange={(event) => setFilename(event.target.files?.[0]?.name ?? '')}
      />
      <span
        aria-hidden="true"
        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border bg-cream text-sm text-ink-soft transition-colors group-hover:border-herb-600/40 group-hover:text-herb-700"
      >
        {icon ?? '↑'}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-ink">
          {filename === '' ? title : filename}
        </span>
        <span className="block text-xs text-ink-faint">{filename === '' ? hint : title}</span>
      </span>
    </label>
  );
}
